/**
 * A community's public homepage, at `tomo.channel/{slug}`.
 *
 * NOT THE ROOM. `/c/{slug}` is the watch app — several streams and one merged
 * chat — and this is the directory in front of it: who is in this community,
 * who is on right now, and a way to find one person among fifty. Two pages
 * because they answer two questions, and the second is the one somebody asks
 * when they have not decided what to watch yet.
 *
 * MOST OF THIS PAGE IS OFFLINE PEOPLE, which is the design constraint. Three
 * live out of fifty is a normal evening, so the offline half is not an
 * afterthought — it is the community. It gets a compact grid rather than
 * being hidden behind a toggle, and the tag filter works across all of it,
 * which is only possible because tags come from each broadcaster's channel
 * rather than from a stream they are not currently running.
 *
 * A COMMUNITY'S OWN GROUPINGS come first when it has them (ADR 0052): they
 * are the community's words for its people, where Twitch tags are each
 * streamer's words for themselves. Groupings are the first filter row and the
 * headings of the offline list; the tags stay, as the second row.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { Grouping, RosterMemberShape } from '@tomo/contracts';
import { Empty, Failed, Spinner } from '../components/primitives.jsx';
import { communities } from '../lib/tomo.js';
import { Landing } from './Landing.jsx';
import { useAsync } from '../useAsync.js';
import {
  byName, filterMembers, liveHeadline, readLiveFirst, rowTags, saveLiveFirst, splitByLive,
  twitchHref, watchHref,
} from '../lib/communityFilter.js';
import { toggleSelection, MAX_SELECTION } from '../lib/frontPage.js';
import { childLabels, headingsFor, matchesGroupings, type Heading } from '../lib/communityGroupings.js';
import { SiteFrame } from '../components/SiteFrame.jsx';
import { useViewer } from '../lib/viewer.js';

/**
 * One array, not a fresh `[]` per render.
 *
 * The memos below take `members` as a dependency, and a new empty array each
 * time would recompute the tag row and the filter on every keystroke while
 * the roster is still loading.
 */
const NOBODY: readonly RosterMemberShape[] = [];
const NO_GROUPINGS: readonly Grouping[] = [];

/** `localStorage`, or null where touching it throws (a sandboxed frame, some private modes). */
function storageOrNull(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Offline cards on first load; each "Show more" adds twice this. */
const OFFLINE_STEP = 48;

export function CommunityHome({ slug }: { readonly slug: string }): ReactNode {
  const [room, retryRoom] = useAsync((signal) => communities.get(slug, signal), [slug]);
  const [roster] = useAsync((signal) => communities.roster(slug, signal), [slug]);
  const viewer = useViewer();
  const [term, setTerm] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  /** The community's groupings picked in the filter. Any of them matches. */
  const [picks, setPicks] = useState<ReadonlySet<string>>(new Set());
  const togglePick = (id: string) => {
    setPicks((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  /**
   * Who to open, in the order they were picked.
   *
   * The same model the front page uses, deliberately: order matters
   * downstream because the first pick is the one that arrives audible, so a
   * Set would quietly decide that on somebody's behalf.
   */
  const [picked, setPicked] = useState<readonly string[]>([]);
  /**
   * LIVE FIRST, or everybody in one list.
   *
   * On, the page is about tonight: who is on, big, then everybody else under
   * their headings. Off, it is a directory — everybody by name under their
   * groupings, the same small card for all, and a LIVE chip on whoever is
   * streaming. A community like Celeste reads its page both ways, so it is a
   * switch, and this browser remembers which way it was left.
   */
  const [liveFirst, setLiveFirstState] = useState(() => readLiveFirst(storageOrNull()));
  const setLiveFirst = (on: boolean) => {
    setLiveFirstState(on);
    saveLiveFirst(storageOrNull(), on);
  };
  const pick = (login: string) => {
    setPicked((current) => toggleSelection(current, login));
  };

  const members = roster.state === 'ready' ? roster.value.data : NOBODY;
  // Only the tags that group somebody — see `rowTags`.
  const tags = useMemo(() => rowTags(members), [members]);
  const groupings = roster.state === 'ready' ? roster.value.groupings : NO_GROUPINGS;
  const shown = useMemo(
    () => filterMembers(members, term, tag).filter((m) => matchesGroupings(m, picks)),
    [members, term, tag, picks],
  );
  const { live, offline } = splitByLive(shown);
  /** What goes under the headings: the offline half, or everybody by name. */
  const listed = useMemo(() => (liveFirst ? offline : byName(shown)), [liveFirst, offline, shown]);
  const { headings, sections } = useMemo(() => headingsFor(listed, groupings), [listed, groupings]);
  const explained = groupings.filter((g) => g.blurb.length > 0);
  /*
   * THE OFFLINE LIST IS PAGED; the live one never is.
   *
   * Live is the reason to be here and is rarely more than a few dozen. The
   * offline list is the rest of a roster that can be 500, and 500 cards is a
   * page nobody scrolls to the bottom of and every phone pays to lay out.
   * The paging belongs to the current filter: typing a name or picking a tag
   * starts from the first page again, rather than keeping a "show more" that
   * was pressed for a different list.
   */
  const filterKey = `${term}\u0000${tag ?? ''}\u0000${[...picks].join(',')}`;
  const [paging, setPaging] = useState({ key: filterKey, count: OFFLINE_STEP });
  const offlineLimit = paging.key === filterKey ? paging.count : OFFLINE_STEP;
  const offlineShown = listed.slice(0, offlineLimit);
  // The headline counts the WHOLE community, not the filtered view: it is a
  // fact about the community, and having it drop to zero because somebody
  // typed a name would read as everyone going offline at once.
  const liveTotal = members.filter((m) => m.live !== null).length;

  if (room.state === 'loading') {
    return <SiteFrame viewer={viewer} trail={[{ label: slug }]}><Spinner label="Loading…" /></SiteFrame>;
  }
  if (room.state === 'failed') {
    /*
     * A NAME THAT IS NOT A COMMUNITY IS NOT AN ERROR.
     *
     * The console answers every apex path the rooms app does not, so this
     * component is what `tomo.channel/anything` reaches. Treating a 404 as a
     * failure would replace the front page with "could not load this
     * community" for every typo, every stale link and every address this
     * product has not invented yet — which is most of them.
     *
     * Anything ELSE is a real failure and says so: a network problem told as
     * "no such community" would send somebody looking for a name that is
     * perfectly fine.
     */
    if (room.error.status === 404) return <Landing />;
    return (
      <SiteFrame viewer={viewer} trail={[{ label: slug }]}>
        <Failed
          message="Could not load this community."
          detail={room.error.message}
          onRetry={retryRoom}
        />
      </SiteFrame>
    );
  }

  return (
    <SiteFrame
      viewer={viewer}
      trail={[{ label: room.value.name }]}
      /*
        IN THE BAR, because the button it carries is assembled further down.
        Picking four streamers means scrolling, and a Watch button that
        scrolled away would make the selection something you have to go back
        for. The bar is sticky on every page now, so this is where it stays.
      */
      actions={
        <>
          {/*
            ONLY FOR THE PEOPLE WHO RUN IT: the owner, the editors they
            appointed, or a platform admin — `your_role` is the server's answer
            for this caller and is absent for everybody else. The admin page
            was reachable only by typing `/admin` after the handle, so an
            editor had no way to know it was there.
          */}
          {room.value.your_role !== undefined ? (
            <a
              href={`/${encodeURIComponent(slug)}/admin`}
              className="rounded-md border border-ink-800 px-3 py-1.5 text-sm text-ink-400 transition hover:border-ink-700 hover:text-ink-50"
            >
              Admin
            </a>
          ) : null}
          {picked.length > 0 ? (
            <button
              type="button"
              onClick={() => { setPicked([]); }}
              className="px-2 py-1 text-xs text-ink-400 hover:text-ink-50"
            >
              Clear {picked.length}
            </button>
          ) : null}
          <a
            href={watchHref(slug, picked)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-400"
          >
            {/*
              Says what it will DO, which changes with the selection. "Watch
              together" over an empty selection promises a room you did not
              choose; naming the count is the difference between a button
              and a guess.
            */}
            {picked.length === 0 ? 'Watch together' : `Watch ${String(picked.length)} together`}
          </a>
        </>
      }
    >
        <h1 className="text-2xl font-semibold">{liveHeadline(liveTotal)}</h1>
        {room.value.description ? (
          <p className="mt-1 max-w-prose text-sm text-ink-400">{room.value.description}</p>
        ) : null}

        <label className="sr-only" htmlFor="community-search">Search streamers</label>
        <input
          id="community-search"
          value={term}
          onChange={(e) => { setTerm(e.target.value); }}
          placeholder="Search streamers…"
          className="mt-5 w-full rounded-md border border-ink-800 bg-ink-900 px-3 py-2 text-sm placeholder:text-ink-700"
        />

        {groupings.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Filter by what they do">
            {groupings.map((g) => (
              <TagPill
                key={g.id}
                label={fullLabel(g, groupings)}
                count={members.filter((m) => m.groupings.includes(g.id)).length}
                active={picks.has(g.id)}
                strong
                onClick={() => { togglePick(g.id); }}
              />
            ))}
            {picks.size > 0 ? (
              <button type="button" onClick={() => { setPicks(new Set()); }} className="px-2 text-xs text-ink-400 hover:text-ink-50">
                Clear
              </button>
            ) : null}
          </div>
        ) : null}

        {explained.length > 0 ? (
          <details className="mt-2 text-xs text-ink-400">
            <summary className="cursor-pointer select-none hover:text-ink-50">What these mean</summary>
            <dl className="mt-2 grid max-w-prose grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {explained.map((g) => (
                <div key={g.id} className="contents">
                  <dt className="font-semibold text-ink-200">{fullLabel(g, groupings)}</dt>
                  <dd>{g.blurb}</dd>
                </div>
              ))}
            </dl>
          </details>
        ) : null}

        {tags.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Filter by Twitch tag">
            <TagPill label={groupings.length > 0 ? 'Any tag' : 'All'} active={tag === null} onClick={() => { setTag(null); }} />
            {tags.map(({ tag: name, count }) => (
              <TagPill
                key={name}
                label={name}
                count={count}
                active={tag === name}
                // Pressing the tag you already have is how you get back to
                // everything, like every other filter in this product.
                onClick={() => { setTag((current) => (current === name ? null : name)); }}
              />
            ))}
          </div>
        ) : null}

        {roster.state === 'ready' && members.length > 0 ? (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              role="switch"
              aria-checked={liveFirst}
              onClick={() => { setLiveFirst(!liveFirst); }}
              className="flex items-center gap-2 text-xs text-ink-200 hover:text-ink-50"
            >
              <span
                aria-hidden
                className={`relative h-4 w-7 rounded-full transition ${liveFirst ? 'bg-brand-500' : 'bg-ink-700'}`}
              >
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-ink-50 transition-all ${liveFirst ? 'left-3.5' : 'left-0.5'}`} />
              </span>
              Live first
            </button>
          </div>
        ) : null}

        {roster.state === 'loading' ? <Spinner label="Loading the roster…" /> : null}
        {roster.state === 'failed' ? (
          <p className="mt-6 text-sm text-ink-400">
            Could not load who is in this community.
          </p>
        ) : null}

        {roster.state === 'ready' && shown.length === 0 ? (
          <Empty
            title="Nobody here matches that."
            hint={picks.size > 0 ? 'Try a different name, or clear the filters.' : 'Try a different name, or clear the tag.'}
          />
        ) : null}

        {liveFirst && live.length > 0 ? (
          <section className="mt-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <span aria-hidden className="h-2 w-2 rounded-full bg-bad-400" />
              Live now
            </h2>
            <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-2">
              {live.map((member) => (
                <LiveCard
                  key={member.login}
                  member={member}
                  labels={childLabels(member, groupings)}
                  picked={picked.includes(member.login)}
                  full={picked.length >= MAX_SELECTION}
                  onPick={() => { pick(member.login); }}
                />
              ))}
            </div>
          </section>
        ) : null}

        {sections.map((section) => (
          <GroupSection
            key={section.id ?? ''}
            heading={section}
            groupings={groupings}
            picked={picked}
            onPick={pick}
          />
        ))}

        {listed.length > 0 && groupings.length > 0 ? (
          <section className="mt-8">
            <h2 className="text-sm font-semibold text-ink-400">
              {/* People only: a section's accounts are counted in their own block. */}
              {liveFirst ? 'Offline' : 'Everyone'}{' '}
              <span className="font-normal">· {listed.length - sections.reduce((n, s) => n + s.members.length, 0)}</span>
            </h2>
            {headings.map((heading) => (
              <GroupSection
                key={heading.id ?? '_rest'}
                heading={heading}
                groupings={groupings}
                picked={picked}
                onPick={pick}
                nested
              />
            ))}
          </section>
        ) : null}

        {listed.length > 0 && groupings.length === 0 ? (
          <section className="mt-8">
            <h2 className="text-sm font-semibold text-ink-400">
              {liveFirst ? 'Offline' : 'Everyone'} <span className="font-normal text-ink-400">· {listed.length}</span>
            </h2>
            <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-2">
              {offlineShown.map((member) => (
                <OfflineCard
                  key={member.login}
                  member={member}
                  labels={[]}
                  picked={picked.includes(member.login)}
                  full={picked.length >= MAX_SELECTION}
                  onPick={() => { pick(member.login); }}
                />
              ))}
            </div>
            {offlineShown.length < listed.length ? (
              <button
                type="button"
                onClick={() => { setPaging({ key: filterKey, count: offlineLimit + OFFLINE_STEP * 2 }); }}
                className="mt-3 rounded-md px-3 py-1.5 text-sm text-ink-200 hover:bg-ink-800 hover:text-ink-50"
              >
                Show {Math.min(OFFLINE_STEP * 2, listed.length - offlineShown.length)} more
                <span className="text-ink-400"> of {listed.length - offlineShown.length}</span>
              </button>
            ) : null}
          </section>
        ) : null}
    </SiteFrame>
  );
}

/** "Speedrun · Vanilla" for a child, so it never reads as some other "Vanilla". */
function fullLabel(g: Grouping, groupings: readonly Grouping[]): string {
  if (g.parent === null) return g.label;
  return `${groupings.find((p) => p.id === g.parent)?.label ?? ''} · ${g.label}`;
}

/**
 * One heading of the offline list, or a section of its own.
 *
 * Each heading pages by itself: a community of 500 grouped five ways would
 * otherwise draw every card under every heading before anybody scrolled.
 */
function GroupSection({
  heading, groupings, picked, onPick, nested = false,
}: {
  readonly heading: Heading<RosterMemberShape>;
  readonly groupings: readonly Grouping[];
  readonly picked: readonly string[];
  readonly onPick: (login: string) => void;
  readonly nested?: boolean;
}): ReactNode {
  const [limit, setLimit] = useState(OFFLINE_STEP);
  const members = heading.members.slice(0, limit);
  return (
    <section className={nested ? 'mt-5' : 'mt-8'}>
      {/*
        A thick rule rather than a card: the heading is a signpost in a long
        list, and it should read as one from a scroll away.
      */}
      <div className="border-l-4 border-brand-400 pl-3">
        <h3 className={nested ? 'text-sm font-semibold text-ink-50' : 'text-sm font-semibold'}>
          {heading.label} <span className="font-normal text-ink-400">· {heading.members.length}</span>
        </h3>
        {heading.blurb ? <p className="text-xs text-ink-400">{heading.blurb}</p> : null}
      </div>
      <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-2">
        {members.map((member) => (
          <OfflineCard
            key={member.login}
            member={member}
            labels={childLabels(member, groupings)}
            picked={picked.includes(member.login)}
            full={picked.length >= MAX_SELECTION}
            onPick={() => { onPick(member.login); }}
          />
        ))}
      </div>
      {members.length < heading.members.length ? (
        <button
          type="button"
          onClick={() => { setLimit(limit + OFFLINE_STEP * 2); }}
          className="mt-2 rounded-md px-3 py-1.5 text-sm text-ink-200 hover:bg-ink-800 hover:text-ink-50"
        >
          Show {Math.min(OFFLINE_STEP * 2, heading.members.length - members.length)} more
        </button>
      ) : null}
    </section>
  );
}

function TagPill({
  label, count, active, onClick, strong = false,
}: {
  readonly label: string;
  readonly count?: number;
  readonly active: boolean;
  readonly onClick: () => void;
  /** The community's own groupings: square, heavier, and first. Tags stay round. */
  readonly strong?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${strong ? 'px-3 py-1.5 text-sm font-medium' : 'rounded-full px-3 py-1 text-xs'} ${
        active ? 'bg-brand-500 font-semibold text-ink-950' : 'bg-ink-900 text-ink-200 hover:bg-ink-800'
      }`}
    >
      {label}
      {count === undefined ? null : <span className="ml-1.5 text-ink-400">{count}</span>}
    </button>
  );
}

/**
 * A shared control, because both card kinds need the same promise.
 *
 * Top-right, away from the links: the card is a place to go and this is a
 * place to add, and putting them under the same press is how somebody ends
 * up on Twitch when they meant to build a grid.
 */
function PickButton({
  member, picked, full, onPick,
}: {
  readonly member: RosterMemberShape;
  readonly picked: boolean;
  readonly full: boolean;
  readonly onPick: () => void;
}): ReactNode {
  // A full selection still lets you REMOVE — otherwise the eighth pick locks
  // the other seven in place.
  const blocked = full && !picked;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={blocked}
      aria-pressed={picked}
      aria-label={picked
        ? `Remove ${member.display_name} from what you will watch`
        : `Add ${member.display_name} to what you will watch`}
      title={blocked ? `That is ${String(MAX_SELECTION)} already` : undefined}
      className={`grid h-7 w-7 place-items-center rounded-full text-sm font-semibold transition ${
        picked
          ? 'bg-brand-500 text-white'
          : 'bg-ink-950/80 text-ink-200 hover:bg-ink-800 disabled:opacity-30'
      }`}
    >
      {picked ? '\u2713' : '+'}
    </button>
  );
}

/** A card's child groupings — "Vanilla" under "Speedrun" — as small flat labels. */
function Labels({ labels }: { readonly labels: readonly string[] }): ReactNode {
  if (labels.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {labels.map((label) => (
        <span key={label} className="bg-ink-800 px-1.5 py-px text-[10px] font-medium text-ink-200 uppercase">
          {label}
        </span>
      ))}
    </span>
  );
}

function LiveCard({
  member, labels, picked, full, onPick,
}: {
  readonly member: RosterMemberShape;
  readonly labels: readonly string[];
  readonly picked: boolean;
  readonly full: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const live = member.live;
  if (!live) return null;
  return (
    <article
      /*
        SQUARE, like the front page's cards. Radius on this product is for
        faces and pills — the things that are already round — and a page of
        rounded rectangles beside a front page of square ones reads as two
        products. The selected state is a ring rather than a border, so
        picking something does not move its contents by a pixel.
      */
      className={`overflow-hidden bg-ink-900 transition ${
        picked ? 'ring-2 ring-brand-500' : 'hover:bg-ink-800'
      }`}
    >
      <div className="relative aspect-video bg-ink-950">
        {/*
          The THUMBNAIL is the link out, not the whole card. A card that is
          entirely a link has nowhere to put a second control that is not also
          the link — which is how a plus button becomes a trip to Twitch.
        */}
        <a
          href={twitchHref(member.login)}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`Watch ${member.display_name} on Twitch`}
          className="block h-full w-full"
        >
          {live.thumbnail_url ? (
            <img
              src={live.thumbnail_url}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : null}
        </a>
        <span className="pointer-events-none absolute top-2 left-2 rounded bg-bad-400 px-1.5 py-0.5 text-[11px] font-semibold text-white">
          LIVE
        </span>
        <span className="absolute top-2 right-2">
          <PickButton member={member} picked={picked} full={full} onPick={onPick} />
        </span>
        <span className="pointer-events-none absolute bottom-2 left-2 rounded bg-ink-950/80 px-1.5 py-0.5 text-[11px] text-ink-50">
          {live.viewer_count} watching
        </span>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <Avatar member={member} size={28} />
          <a
            href={twitchHref(member.login)}
            target="_blank"
            rel="noreferrer noopener"
            className="truncate font-semibold text-ink-50 hover:text-brand-300"
          >
            {member.display_name}
          </a>
        </div>
        <p className="mt-1.5 line-clamp-2 text-xs text-ink-400">
          {live.game_name ? <span className="font-medium text-ink-200">{live.game_name}</span> : null}
          {live.game_name && live.title ? ' \u00b7 ' : null}
          {live.title}
        </p>
        {labels.length > 0 ? <div className="mt-1.5"><Labels labels={labels} /></div> : null}
      </div>
    </article>
  );
}

function OfflineCard({
  member, labels, picked, full, onPick,
}: {
  readonly member: RosterMemberShape;
  readonly labels: readonly string[];
  readonly picked: boolean;
  readonly full: boolean;
  readonly onPick: () => void;
}): ReactNode {
  return (
    <article
      className={`flex items-center gap-2.5 bg-ink-900 px-3 py-2.5 transition ${
        picked ? 'ring-2 ring-brand-500' : 'hover:bg-ink-800'
      }`}
    >
      <Avatar member={member} size={32} />
      <div className="min-w-0 flex-1">
        <a
          href={twitchHref(member.login)}
          target="_blank"
          rel="noreferrer noopener"
          className="block truncate text-sm font-semibold text-ink-50 hover:text-brand-300"
        >
          {member.display_name}
        </a>
        {labels.length > 0 ? <Labels labels={labels} /> : null}
        {member.live ? (
          // In the one-list view a live person gets this card too: the chip
          // says so, and what they are playing replaces the bio.
          <p className="flex min-w-0 items-center gap-1.5 text-xs text-ink-400">
            <span className="shrink-0 rounded bg-bad-400 px-1 py-px text-[10px] font-semibold text-white">LIVE</span>
            <span className="truncate">
              {member.live.game_name || member.live.title || 'Streaming now'}
              {member.live.viewer_count > 0 ? ` \u00b7 ${String(member.live.viewer_count)} watching` : ''}
            </span>
          </p>
        ) : (
          <p className="truncate text-xs text-ink-400">{member.description || 'Offline'}</p>
        )}
      </div>
      {/*
        Offline channels are pickable too, on purpose. You assemble the room
        you want to sit in; somebody who comes on later appears in it without
        anybody going back to the directory to add them.
      */}
      <PickButton member={member} picked={picked} full={full} onPick={onPick} />
    </article>
  );
}

function Avatar({ member, size }: { readonly member: RosterMemberShape; readonly size: number }) {
  if (member.avatar_url) {
    return (
      <img
        src={member.avatar_url}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="shrink-0 rounded-full"
        style={{ width: size, height: size }}
      />
    );
  }
  // Somebody Twitch would not describe still needs a shape in the row.
  return (
    <span
      aria-hidden
      className="grid shrink-0 place-items-center rounded-full bg-ink-800 text-xs font-semibold text-ink-400"
      style={{ width: size, height: size }}
    >
      {member.display_name.slice(0, 1).toUpperCase()}
    </span>
  );
}
